import {trans} from '@frontend/helpers'
import {useLiveSearch} from '@frontend/hooks/useLiveSearch'
import type {
    AdsLiveSearchOptionExtras,
    FilteredItemsPageUrlQueryParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useItemsSearchKeywordsSubmitHandler} from '@frontend/modules/public/ads/components/search/hooks/useItemsSearchKeywordsSubmitHandler'
import {PublicAdsApi} from '@frontend/modules/public/ads/PublicAdsApi'
import {useAdsStore} from '@frontend/stores'
import {iconMagnifier} from '@shared/helpers/icons'
import clsx from 'clsx'
import {useEffect} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {ComboboxInput} from 'swayok-react-mdb-ui-kit/components/Input/ComboboxInput'
import {InputGroup} from 'swayok-react-mdb-ui-kit/components/Input/InputGroup'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {FormSelectOption} from 'swayok-react-mdb-ui-kit/types'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'

interface ItemsSearchKeywordsInputProps {
    // Макет: кнопка поиска всегда только с иконкой. На мобильной
    // она лежит поверх поля, на остальных - отдельным блоком слева.
    buttonMode: 'icon' | 'floating-icon'
}

// Поле ввода ключевых слов для поиска объявлений.
export function ItemsSearchKeywordsInput(props: ItemsSearchKeywordsInputProps) {

    const {
        buttonMode,
    } = props

    const isSearchPanelDisabled = useAdsStore(
        state => state.isSearchPanelDisabled
    )
    const keywords = useAdsStore(
        state => state.searchPanelKeywords
    )
    const setKeywords = useAdsStore(
        state => state.setSearchPanelKeywords
    )
    const setCategoryById = useAdsStore(
        state => state.setSearchPanelCategoryById
    )

    // Данные о текущем URL.
    const {
        urlQueryParams,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()

    // Загрузчик опций для Live Search.
    const {
        options: quickSearchOptions,
        reset: resetLiveSearch,
        onKeywordsChange: loadLiveSearchOptions,
    } = useLiveSearch(
        PublicAdsApi.liveSearch
    )

    // Нажатие кнопки "Найти" для ключевых слов.
    const onKeywordsSubmit = useItemsSearchKeywordsSubmitHandler()

    // Сбрасываем набор опций при сбросе keywords в пустую строку.
    useEffect(() => {
        if (keywords === '') {
            resetLiveSearch()
        }
    }, [keywords])

    // Синхронизируем keywords с URL query.
    useEffect(() => {
        if (urlQueryParams.has('keywords') && urlQueryParams.get('keywords') !== keywords) {
            setKeywords(urlQueryParams.get('keywords') ?? '')
        }
    }, [urlQueryParams])

    // Изменение значения поля ввода или выбор опции из выпадающего меню.
    const onChange = useEventCallback((
        value: string,
        option?: FormSelectOption<string, AdsLiveSearchOptionExtras>
    ) => {
        // Если нет опции, то произошел ввод символа в поле ввода.
        if (!option) {
            loadLiveSearchOptions(value)
            setKeywords(value)
            return
        }

        // Выбрана опция из выпадающего меню.

        // Начать поиск по ключевым словам и фильтрам.
        const submitKeywords = (
            keywords: string,
            filters?: AnyObject
        ) => {
            setKeywords(keywords)
            onKeywordsSubmit(undefined, filters)
            resetLiveSearch()
        }

        // Чистим ключевые слова от HTML и префикса.
        const keywords: string = option.label
            // Удаляем всё до '&larr;' или '&rarr;' включительно.
            .replace(/^.*&[lr]arr;\s+/i, '')
            // Чистим от HTML тегов для отображения в поле ввода.
            .replace(/<[^>]+?>/g, '')

        // Если известен только текст, то сразу ищем
        // по ключевым словам без изменения категории.
        if (!option.extra?.categoryId) {
            submitKeywords(keywords)
            return
        }

        // Если это категория, то переходим на страницу категории.
        if (option.extra.type === 'category') {
            setCategoryById(option.extra.categoryId)
            submitKeywords('')
            return
        }

        // Объявление или исполнитель услуг.

        // Меняем категорию, если требуется.
        if (option.extra.switchCategory) {
            // Требуется сменить категорию.
            setCategoryById(option.extra.categoryId)
        }

        // Если указан тип услуг, то переходим в категорию с фильтром по типу услуг.
        if (
            option.extra.type === 'service_type'
            && option.extra.serviceTypeId
        ) {
            // Фильтрация по типу услуг.
            submitKeywords('', {
                services: [option.extra.serviceTypeId],
            })
            return
        }

        // Объявление или исполнитель услуг.
        submitKeywords(keywords)
        return
    })

    const input = (
        <ComboboxInput<string, AdsLiveSearchOptionExtras>
            label={trans().common.ads_filter.placeholder}
            type={buttonMode === 'floating-icon' ? 'text' : 'search'}
            value={keywords}
            options={keywords === '' ? [] : quickSearchOptions}
            optionsFiltering={false}
            focusFirstItemOnOpen={false}
            optionLabelIsHtml={true}
            name="keywords"
            disabled={isSearchPanelDisabled}
            wrapperClassName="flex-1 offered-items-filter-keywords-input"
            className="bg-white"
            onChange={onChange}
        />
    )

    return (
        <form
            className={clsx(
                'offered-items-filter-keywords-form flex-1',
                'button-mode-' + buttonMode
            )}
            onSubmit={onKeywordsSubmit}
        >
            <InputGroup className="m-0">
                <Button
                    color={buttonMode === 'floating-icon' ? 'white' : 'blue'}
                    type="submit"
                    hasIcon
                    disabled={isSearchPanelDisabled}
                >
                    <Icon
                        path={iconMagnifier}
                        color={buttonMode === 'floating-icon' ? 'muted' : 'white'}
                        size={22}
                    />
                </Button>
                {input}
            </InputGroup>
        </form>
    )
}
